import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Send, Search, Loader2, Check } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * Forward an existing job note to one or more teammates.
 *
 * Lightweight picker over /api/users — admin/supervisor/user all welcome.
 * Excludes the current user (you can't forward to yourself) and anyone
 * already in the note's `notify_recipient_ids` (de-dupes the "📩 notified"
 * badge so we don't re-send to the same person twice).
 */
export default function ForwardNoteDialog({ open, onClose, note, onSubmit }) {
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState({}); // {userId: true}
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked({});
    setSearch('');
    const token = localStorage.getItem('token');
    axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const me = JSON.parse(localStorage.getItem('user') || '{}');
        const already = new Set(note?.notify_recipient_ids || []);
        const list = (r.data || []).filter((u) =>
          u.id !== me.id && !u.deleted_at && !already.has(u.id)
        );
        setUsers(list);
      })
      .catch(() => setUsers([]));
  }, [open, note?.id, note?.notify_recipient_ids]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users.slice(0, 25);
    return users.filter((u) => [u.name, u.email, u.role]
      .some((v) => (v || '').toLowerCase().includes(q))).slice(0, 25);
  }, [users, search]);

  const pickedIds = Object.entries(picked).filter(([, v]) => v).map(([k]) => k);
  const canSubmit = pickedIds.length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(pickedIds);
    } finally {
      setSubmitting(false);
    }
  };

  if (!note) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1A2433] text-white max-w-md" data-testid="forward-note-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-400" />
            Forward note to a teammate
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-slate-400 bg-[#0F1923] rounded p-2 border-l-2 border-blue-500/40 max-h-20 overflow-y-auto">
          {(note.text || '').slice(0, 240)}
          {(note.text || '').length > 240 ? '…' : ''}
        </div>

        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search teammates by name, email, role…"
              className="pl-8 bg-[#0F1923] border-[#2D3748] text-white text-sm"
              data-testid="forward-note-search"
            />
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1 border border-[#2D3748] rounded p-2" data-testid="forward-note-list">
            {filtered.length === 0 ? (
              <p className="text-xs text-slate-500 italic px-1 py-1">
                {users.length === 0 ? 'Nobody else to forward to.' : 'No matches.'}
              </p>
            ) : filtered.map((u) => {
              const isPicked = !!picked[u.id];
              return (
                <button
                  type="button"
                  key={u.id}
                  onClick={() => setPicked((p) => ({ ...p, [u.id]: !p[u.id] }))}
                  className={`w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded text-left text-sm transition ${
                    isPicked ? 'bg-blue-500/15 border border-blue-500/50' : 'hover:bg-blue-500/10 border border-transparent'
                  }`}
                  data-testid={`forward-note-user-${u.id}`}
                >
                  <div>
                    <span className="font-medium text-white">{u.name}</span>
                    <span className="text-slate-400 ml-2 text-xs">{u.email} · {u.role}</span>
                  </div>
                  {isPicked && <Check className="w-4 h-4 text-blue-300" />}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500">
            Recipients get a copy in their personal Notes inbox AND an in-app notification.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="forward-note-cancel">Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="forward-note-send"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Forward to {pickedIds.length || ''} teammate{pickedIds.length === 1 ? '' : 's'}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
